/**
 * CORE_REGISTRY — Central dispatch for all Word tool executors.
 *
 * Mirrors the Excel adapter pattern. Each entry maps a tool name to a
 * core function that receives the Word `RequestContext` and optional
 * arguments. Core functions queue loads/writes on proxy objects; the
 * `executeWordTool` wrapper handles the final `context.sync()`.
 *
 * Self-syncing tools call `ctx.sync()` internally (reads, search,
 * comment listing/creation). Queue-only tools just set properties on
 * proxy objects — they batch nicely until a self-syncing tool or the
 * wrapper's final sync flushes them.
 *
 * @see ADR-043 — Host-Neutral Client-Side Tool Protocol
 * @see apps/web/lib/excel/tool-executors/index.ts
 */

import type { ClientToolExecutorAdapter } from "@magnus/react/copilot-tools";
import {
  assertBatchTools,
  type BatchOperation,
  type BatchOperationResult,
  raceWithTimeout,
  shouldSyncAfterOp,
  summarizeBatch,
  sumToolTimeouts,
} from "@/lib/office/run-executor";
import {
  addCommentCore,
  deleteCommentCore,
  getCommentsCore,
  replyToCommentCore,
  resolveCommentCore,
} from "./comments";
import { saveDocumentCore } from "./doc";
import {
  formatSelectionCore,
  setParagraphAlignmentCore,
  setParagraphSpacingCore,
  setParagraphStyleCore,
} from "./formatting";
import { getDocumentInfoCore, getSelectionInfoCore } from "./inspection";
import {
  readDocumentBodyCore,
  readParagraphsCore,
  readSelectionCore,
} from "./read";
import { findReplaceCore, searchDocumentCore } from "./search";
import { getTablesCore, insertTableCore } from "./tables";
import {
  acceptTrackedChangesCore,
  getTrackedChangesCore,
  rejectTrackedChangesCore,
  setTrackChangesCore,
} from "./track-changes";
import { formatOfficeError } from "./utils";
import {
  clearSelectionCore,
  deleteSelectionCore,
  insertBreakCore,
  insertHtmlCore,
  insertParagraphCore,
  insertTextCore,
  replaceSelectionCore,
} from "./write";

type CoreFn = (
  ctx: Word.RequestContext,
  args?: Record<string, unknown>
) => Promise<unknown> | unknown;

const CORE_REGISTRY: Record<string, CoreFn> = {
  // Inspection
  get_document_info: getDocumentInfoCore,
  get_selection_info: getSelectionInfoCore,
  // Read
  read_document_body: readDocumentBodyCore,
  read_paragraphs: readParagraphsCore,
  read_selection: readSelectionCore,
  // Write
  insert_text: insertTextCore,
  insert_paragraph: insertParagraphCore,
  insert_break: insertBreakCore,
  insert_html: insertHtmlCore,
  replace_selection: replaceSelectionCore,
  delete_selection: deleteSelectionCore,
  clear_selection: clearSelectionCore,
  // Formatting
  format_selection: formatSelectionCore,
  set_paragraph_alignment: setParagraphAlignmentCore,
  set_paragraph_style: setParagraphStyleCore,
  set_paragraph_spacing: setParagraphSpacingCore,
  // Search
  search_document: searchDocumentCore,
  find_replace: findReplaceCore,
  // Tables
  insert_table: insertTableCore,
  get_tables: getTablesCore,
  // Comments
  add_comment: addCommentCore,
  get_comments: getCommentsCore,
  reply_to_comment: replyToCommentCore,
  resolve_comment: resolveCommentCore,
  delete_comment: deleteCommentCore,
  // Track Changes
  set_track_changes: setTrackChangesCore,
  get_tracked_changes: getTrackedChangesCore,
  accept_tracked_changes: acceptTrackedChangesCore,
  reject_tracked_changes: rejectTrackedChangesCore,
  // Document
  save_document: saveDocumentCore,
};

const TOOL_TIMEOUTS: Record<string, number> = {
  get_document_info: 15_000,
  get_selection_info: 10_000,
  read_document_body: 60_000,
  read_paragraphs: 30_000,
  read_selection: 10_000,
  insert_text: 15_000,
  insert_paragraph: 15_000,
  insert_break: 10_000,
  insert_html: 30_000,
  replace_selection: 15_000,
  delete_selection: 10_000,
  clear_selection: 10_000,
  format_selection: 15_000,
  set_paragraph_alignment: 10_000,
  set_paragraph_style: 10_000,
  set_paragraph_spacing: 10_000,
  search_document: 30_000,
  find_replace: 60_000,
  insert_table: 30_000,
  get_tables: 30_000,
  add_comment: 15_000,
  get_comments: 30_000,
  reply_to_comment: 15_000,
  resolve_comment: 10_000,
  delete_comment: 10_000,
  set_track_changes: 10_000,
  get_tracked_changes: 30_000,
  accept_tracked_changes: 30_000,
  reject_tracked_changes: 30_000,
  save_document: 10_000,
};
const DEFAULT_TIMEOUT = 30_000;

/**
 * Self-syncing tools — these call `ctx.sync()` internally. When used in
 * a batch, the loop does NOT need an additional sync after them because
 * their internal sync also flushes any prior queued proxy operations from
 * preceding queue-only tools.
 *
 * Everything NOT in this set is "queue-only" — it just sets properties
 * or queues inserts on proxy objects and relies on a later sync to flush.
 */
const SELF_SYNCING_TOOLS: ReadonlySet<string> = new Set([
  "get_document_info",
  "get_selection_info",
  "read_document_body",
  "read_paragraphs",
  "read_selection",
  "search_document",
  "find_replace",
  "get_tables",
  "add_comment",
  "get_comments",
  "reply_to_comment",
  "resolve_comment",
  "delete_comment",
  "replace_selection",
  "delete_selection",
  "clear_selection",
  "get_tracked_changes",
  "accept_tracked_changes",
  "reject_tracked_changes",
]);

export const WORD_TOOL_NAMES: ReadonlySet<string> = new Set([
  ...Object.keys(CORE_REGISTRY),
  "batch_operations",
]);

export const wordClientToolAdapter: ClientToolExecutorAdapter = {
  execute: executeWordTool,
  toolNames: WORD_TOOL_NAMES,
};

export async function executeWordTool(
  toolName: string,
  args: Record<string, unknown>
): Promise<unknown> {
  if (toolName === "batch_operations") {
    return executeBatchOperations(args);
  }

  const coreFn = CORE_REGISTRY[toolName];
  if (!coreFn) {
    throw new Error(`Unknown Word tool: ${toolName}`);
  }

  if (typeof Word === "undefined" || !Word.run) {
    throw new Error(
      "Word API is not available. Ensure you are running inside the Word add-in."
    );
  }

  const timeout = TOOL_TIMEOUTS[toolName] ?? DEFAULT_TIMEOUT;

  const wordPromise = Word.run(async (context) => {
    try {
      // Suppress screen repaints for the duration of this tool call.
      // Cast needed: Word type defs don't expose document.application on all builds.
      (
        context.document as any
      ).application.suspendScreenUpdatingUntilNextSync();
    } catch {
      // Not available on older Word builds — safe to skip.
    }
    const result = await coreFn(context, args);
    await context.sync();
    return result;
  });

  try {
    return await raceWithTimeout(
      wordPromise,
      timeout,
      `Word tool "${toolName}" timed out after ${timeout / 1000}s. ` +
        "Word may be busy — close any dialogs and try again."
    );
  } catch (err) {
    // Word, unlike Excel, has no runtime-recovery step on timeout — its run
    // doesn't disable events. (Kept explicit so the asymmetry is visible.)
    throw new Error(formatOfficeError(err));
  }
}

// ---------------------------------------------------------------------------
// Batch operations
// ---------------------------------------------------------------------------

async function executeBatchOperations(
  args: Record<string, unknown>
): Promise<{ results: BatchOperationResult[]; summary: string }> {
  const operations = (args.operations ?? []) as BatchOperation[];

  if (operations.length === 0) {
    return { results: [], summary: "No operations provided." };
  }

  assertBatchTools(operations, CORE_REGISTRY, "Word");

  if (typeof Word === "undefined" || !Word.run) {
    throw new Error(
      "Word API is not available. Ensure you are running inside the Word add-in."
    );
  }

  const totalTimeout = sumToolTimeouts(
    operations,
    TOOL_TIMEOUTS,
    DEFAULT_TIMEOUT
  );

  const wordPromise = Word.run(async (context) => {
    try {
      // Cast needed: Word type defs don't expose document.application on all builds.
      (
        context.document as any
      ).application.suspendScreenUpdatingUntilNextSync();
    } catch {
      // Not available on older Word builds — safe to skip.
    }

    const results: BatchOperationResult[] = [];

    try {
      for (let i = 0; i < operations.length; i++) {
        const op = operations[i] as BatchOperation;
        const coreFn = CORE_REGISTRY[op.tool] as CoreFn;

        try {
          const result = await coreFn(context, op.args ?? {});

          if (shouldSyncAfterOp(i, operations, SELF_SYNCING_TOOLS)) {
            await context.sync();
          }

          results.push({ tool: op.tool, status: "success", result });
        } catch (opErr) {
          results.push({
            tool: op.tool,
            status: "error",
            error: formatOfficeError(opErr),
          });
        }
      }
    } finally {
      await context.sync();
    }

    return results;
  });

  try {
    const results = await raceWithTimeout(
      wordPromise,
      totalTimeout,
      `Batch operations timed out after ${totalTimeout / 1000}s. ` +
        "Word may be busy — close any dialogs and try again."
    );
    return { results, summary: summarizeBatch(results) };
  } catch (err) {
    throw new Error(formatOfficeError(err));
  }
}
